import React, { useState } from "react";
import {
    Box,
    Button,
    TextField,
    Typography,
    Backdrop,
    CircularProgress,
    Card,
    CardContent,
    Grid,
    Chip,
} from "@mui/material";
import { Business, Update } from "@mui/icons-material";
import { blue } from "@mui/material/colors";
import axiosInstance from "../../api/axiosInstance";
import base_url from "../../constants";
import useNotification from "../common/Notification";
import { WB_SX, WB_UI } from "../../ui/ux/uiTokens";

const BusinessProfile = () => {
    const [loading, setLoading] = useState(false);
    const [phoneNumberId, setPhoneNumberId] = useState("");
    const [profile, setProfile] = useState(null);
    const [editMode, setEditMode] = useState(false);
    const [profileForm, setProfileForm] = useState({
        address: "",
        description: "",
        email: "",
        websites: [],
        vertical: "",
    });
    const showNotification = useNotification();

    const fetchProfile = async () => {
        // Phone Number ID can be optional if stored in config
        const phoneNumberIdToUse = phoneNumberId.trim() || null;
        if (!phoneNumberIdToUse) {
            showNotification("Please enter Phone Number ID or configure it in Settings", "error");
            return;
        }

        setLoading(true);
        try {
            // Backend will automatically use config from database based on authenticated user
            const response = await axiosInstance.get(
                `${base_url}/whatsapp-business/phone-number/${phoneNumberIdToUse}/business-profile`
            );

            setProfile(response.data);
            setProfileForm({
                address: response.data.address || "",
                description: response.data.description || "",
                email: response.data.email || "",
                websites: response.data.websites || [],
                vertical: response.data.vertical || "",
            });
            showNotification("Business profile fetched successfully!", "success");
        } catch (error) {
            console.error("Error fetching business profile:", error);
            showNotification(
                error.response?.data?.error || "Failed to fetch business profile",
                "error"
            );
        } finally {
            setLoading(false);
        }
    };

    const handleUpdateProfile = async () => {
        // Phone Number ID can be optional if stored in config
        const phoneNumberIdToUse = phoneNumberId.trim() || null;
        if (!phoneNumberIdToUse) {
            showNotification("Please enter Phone Number ID or configure it in Settings", "error");
            return;
        }

        setLoading(true);
        try {
            // Backend will automatically use config from database based on authenticated user
            await axiosInstance.post(
                `${base_url}/whatsapp-business/phone-number/${phoneNumberIdToUse}/business-profile`,
                profileForm
            );

            showNotification("Business profile updated successfully!", "success");
            setEditMode(false);
            fetchProfile();
        } catch (error) {
            console.error("Error updating business profile:", error);
            showNotification(
                error.response?.data?.error || "Failed to update business profile",
                "error"
            );
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box sx={WB_SX.page}>
            <Typography
                variant="h6"
                sx={WB_SX.pageTitle}
            >
                Business Profile
            </Typography>

            <Backdrop
                sx={(theme) => ({
                    color: "#fff",
                    zIndex: theme.zIndex.drawer + 1,
                })}
                open={loading}
            >
                <CircularProgress color="inherit" />
            </Backdrop>

            <Card sx={{ mb: WB_UI.spacing.sectionGap, ...WB_SX.card }}>
                <CardContent sx={WB_SX.cardContent}>
                    <Box sx={{ display: "flex", gap: 2, mb: 2, flexWrap: "wrap" }}>
                        <TextField
                            label="Phone Number ID (Optional - override saved value)"
                            variant="outlined"
                            size="small"
                            value={phoneNumberId}
                            onChange={(e) => setPhoneNumberId(e.target.value)}
                            placeholder="Enter Phone Number ID to override saved value"
                            sx={{ flex: 1, minWidth: "200px" }}
                            helperText="Leave empty to use saved value from Settings"
                        />
                        <Button
                            variant="contained"
                            onClick={fetchProfile}
                            startIcon={<Business />}
                            sx={{ minWidth: "150px", ...WB_SX.primaryButton }}
                        >
                            Fetch Profile
                        </Button>
                    </Box>
                </CardContent>
            </Card>

            {profile && (
                <Card sx={WB_SX.card}>
                    <CardContent sx={WB_SX.cardContent}>
                    <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
                            <Typography variant="h6" sx={{ fontWeight: 800, letterSpacing: "-0.02em" }}>
                                Profile Details
                            </Typography>
                            <Button
                                variant={editMode ? "outlined" : "contained"}
                                onClick={() => {
                                    if (editMode) {
                                        handleUpdateProfile();
                                    } else {
                                        setEditMode(true);
                                    }
                                }}
                                startIcon={<Update />}
                                sx={editMode ? WB_SX.secondaryButton : WB_SX.primaryButton}
                            >
                                {editMode ? "Save Changes" : "Edit Profile"}
                            </Button>
                        </Box>

                        {editMode ? (
                            <Grid container spacing={2}>
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        label="Address"
                                        value={profileForm.address}
                                        onChange={(e) =>
                                            setProfileForm({
                                                ...profileForm,
                                                address: e.target.value,
                                            })
                                        }
                                        multiline
                                        rows={2}
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        label="Description"
                                        value={profileForm.description}
                                        onChange={(e) =>
                                            setProfileForm({
                                                ...profileForm,
                                                description: e.target.value,
                                            })
                                        }
                                        multiline
                                        rows={3}
                                    />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <TextField
                                        fullWidth
                                        label="Email"
                                        type="email"
                                        value={profileForm.email}
                                        onChange={(e) =>
                                            setProfileForm({
                                                ...profileForm,
                                                email: e.target.value,
                                            })
                                        }
                                    />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <TextField
                                        fullWidth
                                        label="Vertical"
                                        value={profileForm.vertical}
                                        onChange={(e) =>
                                            setProfileForm({
                                                ...profileForm,
                                                vertical: e.target.value,
                                            })
                                        }
                                        placeholder="e.g., RETAIL, ECOMMERCE"
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <Button
                                        variant="outlined"
                                        onClick={() => setEditMode(false)}
                                        sx={{ mr: 2 }}
                                    >
                                        Cancel
                                    </Button>
                                </Grid>
                            </Grid>
                        ) : (
                            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                                {profile.address && (
                                    <Typography>
                                        <strong>Address:</strong> {profile.address}
                                    </Typography>
                                )}
                                {profile.description && (
                                    <Typography>
                                        <strong>Description:</strong> {profile.description}
                                    </Typography>
                                )}
                                {profile.email && (
                                    <Typography>
                                        <strong>Email:</strong> {profile.email}
                                    </Typography>
                                )}
                                {profile.vertical && (
                                    <Typography>
                                        <strong>Vertical:</strong>{" "}
                                        <Chip label={profile.vertical} size="small" />
                                    </Typography>
                                )}
                                {profile.websites && profile.websites.length > 0 && (
                                    <Box>
                                        <Typography sx={{ mb: 1 }}>
                                            <strong>Websites:</strong>
                                        </Typography>
                                        {profile.websites.map((website, index) => (
                                            <Chip
                                                key={index}
                                                label={website}
                                                sx={{ mr: 1, mb: 1 }}
                                            />
                                        ))}
                                    </Box>
                                )}
                            </Box>
                        )}
                    </CardContent>
                </Card>
            )}
        </Box>
    );
};

export default BusinessProfile;

